// BMAP reader. Direct port of `readmapfile` + `read_run` from Cheshire's
// BoloMapFile.c (in references/Bolo f/More information/Sample Code/Map File Format/).

import { MAP_SIZE, MAX_BASES, MAX_PILLS, MAX_STARTS, Terrain, TERRAIN_MINE } from '@bolo2k/shared';
import { BMAP_MAGIC, BMAP_VERSION, type BMapData, TERRAIN_BYTES } from './types.js';

export class BMapParseError extends Error {
  constructor(message: string, public readonly offset?: number) {
    super(message);
    this.name = 'BMapParseError';
  }
}

// Convert a disk terrain nibble (0-15) to runtime byte (terrain | mine bit).
// Disk 0-9 are non-mined; disk 10-15 are mined variants of 2-7.
// (See BoloMapFile.h: "10-15 are 2-7 with mines on them".)
function diskNibbleToRuntime(nibble: number): number {
  if (nibble < 10) return nibble;
  // Mined variant: subtract 8 to recover the base terrain code (2-7), set mine bit.
  return (nibble - 8) | TERRAIN_MINE;
}

export function parseBMap(buf: Uint8Array): BMapData {
  if (buf.length < 12) {
    throw new BMapParseError('file shorter than 12-byte preamble', 0);
  }

  // Preamble: 8-byte magic, version, npills, nbases, nstarts.
  const magic = String.fromCharCode(...buf.subarray(0, 8));
  if (magic !== BMAP_MAGIC) {
    throw new BMapParseError(`bad magic: expected "${BMAP_MAGIC}", got "${magic}"`, 0);
  }
  const version = buf[8]!;
  if (version !== BMAP_VERSION) {
    throw new BMapParseError(`unsupported version ${version}`, 8);
  }
  const numPills = buf[9]!;
  const numBases = buf[10]!;
  const numStarts = buf[11]!;

  if (numPills > MAX_PILLS) throw new BMapParseError(`too many pillboxes: ${numPills}`, 9);
  if (numBases > MAX_BASES) throw new BMapParseError(`too many bases: ${numBases}`, 10);
  if (numStarts > MAX_STARTS) throw new BMapParseError(`too many starts: ${numStarts}`, 11);

  let off = 12;
  const expectedHeader = 12 + numPills * 5 + numBases * 6 + numStarts * 3;
  if (buf.length < expectedHeader) {
    throw new BMapParseError(
      `file truncated: header needs ${expectedHeader} bytes, have ${buf.length}`,
      off,
    );
  }

  const pillboxes = Array.from({ length: numPills }, () => {
    const p = { x: buf[off]!, y: buf[off + 1]!, owner: buf[off + 2]!, armour: buf[off + 3]!, speed: buf[off + 4]! };
    off += 5;
    return p;
  });
  const bases = Array.from({ length: numBases }, () => {
    const b = {
      x: buf[off]!,
      y: buf[off + 1]!,
      owner: buf[off + 2]!,
      armour: buf[off + 3]!,
      shells: buf[off + 4]!,
      mines: buf[off + 5]!,
    };
    off += 6;
    return b;
  });
  const starts = Array.from({ length: numStarts }, () => {
    const s = { x: buf[off]!, y: buf[off + 1]!, dir: buf[off + 2]! };
    off += 3;
    return s;
  });

  // Terrain: pre-fill with DeepSea (10), then overwrite per run.
  const terrain = new Uint8Array(TERRAIN_BYTES);
  terrain.fill(Terrain.DeepSea);

  // Read RLE runs until end-sentinel { datalen=4, y=0xFF, startx=0xFF, endx=0xFF }.
  while (off + 4 <= buf.length) {
    const datalen = buf[off]!;
    const y = buf[off + 1]!;
    const startx = buf[off + 2]!;
    const endx = buf[off + 3]!;

    if (datalen === 4 && y === 0xff && startx === 0xff && endx === 0xff) {
      off += 4;
      return { version, pillboxes, bases, starts, terrain };
    }

    if (datalen < 4) {
      throw new BMapParseError(`run datalen ${datalen} < 4 at offset ${off}`, off);
    }
    if (off + datalen > buf.length) {
      throw new BMapParseError(
        `run at offset ${off} claims ${datalen} bytes but only ${buf.length - off} remain`,
        off,
      );
    }
    if (startx > endx) {
      throw new BMapParseError(`run startx ${startx} > endx ${endx} at offset ${off}`, off);
    }

    const dataStart = off + 4;
    const dataEnd = off + datalen;
    let nibblePos = dataStart * 2; // bit-position into the byte stream, in nibbles
    let x = startx;

    while (x < endx) {
      if (nibblePos >> 1 >= dataEnd) {
        throw new BMapParseError(`run data exhausted before reaching endx at offset ${off}`, off);
      }
      const code = readNibble(buf, nibblePos++);
      if (code < 8) {
        // Sequence of (code+1) different squares.
        const count = code + 1;
        for (let i = 0; i < count; i++) {
          if (x >= endx) {
            throw new BMapParseError(
              `run overflow: nibble would write past endx (off ${off})`,
              off,
            );
          }
          if (nibblePos >> 1 >= dataEnd) {
            throw new BMapParseError(`run data exhausted mid-sequence at offset ${off}`, off);
          }
          const t = readNibble(buf, nibblePos++);
          terrain[y * MAP_SIZE + x] = diskNibbleToRuntime(t);
          x++;
        }
      } else {
        // Sequence of (code-6) identical squares.
        const count = code - 6;
        const t = readNibble(buf, nibblePos++);
        const runtime = diskNibbleToRuntime(t);
        for (let i = 0; i < count; i++) {
          if (x >= endx) {
            throw new BMapParseError(
              `run overflow: repeat would write past endx (off ${off})`,
              off,
            );
          }
          terrain[y * MAP_SIZE + x] = runtime;
          x++;
        }
      }
    }

    if (x !== endx) {
      throw new BMapParseError(`run ended at x=${x}, expected endx=${endx} (off ${off})`, off);
    }

    off += datalen;
  }

  throw new BMapParseError('reached EOF without end sentinel', off);
}

function readNibble(buf: Uint8Array, nibblePos: number): number {
  const byte = buf[nibblePos >> 1]!;
  return (nibblePos & 1) === 0 ? byte >> 4 : byte & 0x0f;
}
